/**
 * Content drawer – trigger opens a right-side drawer with heading and content.
 */
class ContentDrawer extends HTMLElement {
  constructor() {
    super();
    this._overlay = this.querySelector('[data-content-drawer-overlay]');
    this._closeBtn = this.querySelector('[data-content-drawer-close]');
    this.close = this.close.bind(this);
  }

  connectedCallback() {
    const id = this.dataset.drawerId;
    if (!id) return;

    this._originalParent = this.parentNode;
    this._trigger = document.querySelector(`[data-content-drawer-trigger="${id}"]`);
    this._onTriggerClick = () => this.open();
    this._onKeyUp = (e) => { if (e.code === 'Escape') this.close(); };
    if (this._trigger) this._trigger.addEventListener('click', this._onTriggerClick);
    this.addEventListener('keyup', this._onKeyUp);
    if (this._overlay) this._overlay.addEventListener('click', this.close);
    if (this._closeBtn) this._closeBtn.addEventListener('click', this.close);
  }

  disconnectedCallback() {
    if (this._trigger) this._trigger.removeEventListener('click', this._onTriggerClick);
    this.removeEventListener('keyup', this._onKeyUp);
  }

  open() {
    this.activeElement = this._trigger || document.activeElement;
    if (this._trigger) this._trigger.setAttribute('aria-expanded', 'true');
    if (this._originalParent && this.parentNode !== document.body) {
      document.body.appendChild(this);
    }
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');

    // Force browser to paint closed state before adding .active, so open transition always runs
    this.offsetHeight;
    requestAnimationFrame(() => {
      this.classList.add('active');
    });

    const inner = this.querySelector('.content-drawer__inner') || this.querySelector('.drawer__inner');
    const done = () => {
      if (typeof trapFocus === 'function') trapFocus(this, inner);
      else if (this._closeBtn) this._closeBtn.focus();
    };
    if (inner) inner.addEventListener('transitionend', done, { once: true });
    else done();
  }

  close() {
    if (!this.classList.contains('active')) return;
    if (this._trigger) this._trigger.setAttribute('aria-expanded', 'false');
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('active');

    const inner = this.querySelector('.content-drawer__inner') || this.querySelector('.drawer__inner');
    const done = () => {
      document.body.classList.remove('overflow-hidden');
      if (this._originalParent && this.parentNode === document.body) {
        this._originalParent.appendChild(this);
      }
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
      if (this.activeElement && this.activeElement.focus) this.activeElement.focus();
    };
    if (inner) inner.addEventListener('transitionend', done, { once: true });
    else done();
  }
}

if (!customElements.get('content-drawer')) {
  customElements.define('content-drawer', ContentDrawer);
}
